import { useQueries, useQuery } from "@tanstack/react-query";
import { isShownState } from "@shared/customerTypeMenu";
import { getApiPath } from "@/lib/queryClient";
import {
  CUSTOMER_TYPES_PATH,
  customerTypePath,
  previewTypeTick,
  type CustomerTypeDetail,
  type CustomerTypeSummary,
} from "@/lib/customerTypes";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

/**
 * Product form → Customer types: one "Shown to <type>" tick per type, for orgs that have
 * types. A new product is shown to every type unless a type hides its whole category;
 * untick to hide it from one type. The ticks read the same rules as the customer menu
 * (client/src/lib/customerTypes.ts mirrors server/services/menuPricing.ts) and are saved
 * with the form; nothing is written until then.
 */
export function ProductCustomerTypesSection({
  productId,
  categoryId,
  categoryName,
  choices,
  onChoice,
}: {
  /** null while the product is being created. */
  productId: number | null;
  /** The form's unit, as it stands. Not read: a type never hides by sell unit. */
  unit?: string;
  /** The form's category as it stands, saved or not. */
  categoryId: number | null;
  categoryName: string | null;
  /** The ticks the admin has changed in this form, by type id. */
  choices: Readonly<Record<number, boolean>>;
  onChoice: (customerTypeId: number, shown: boolean) => void;
}) {
  const { data: types } = useQuery<CustomerTypeSummary[]>({ queryKey: [getApiPath(CUSTOMER_TYPES_PATH)] });
  const details = useQueries({
    queries: (types ?? []).map((type) => ({ queryKey: [getApiPath(customerTypePath(type.id))] })),
  });

  // No types in this org (or still loading): the form stays as it was.
  if (!types || types.length === 0) return null;

  const product = { id: productId ?? 0, categoryId };

  return (
    <div className="rounded-lg border border-gray-200 p-3" data-testid="product-customer-types-section">
      <Label>Customer types</Label>
      <p className="mt-0.5 text-xs text-gray-500">Which types of customer see this product on their menu.</p>
      <ul className="mt-2 space-y-2">
        {types.map((type, i) => {
          const detail = details[i]?.data as CustomerTypeDetail | undefined;
          const failed = detail === undefined && details[i]?.isError === true;
          const { state } = detail ? previewTypeTick(product, detail, choices[type.id]) : { state: "shown" as const };
          const byCategory = state === "hidden_by_category";
          const note = !detail
            ? failed
              ? `Couldn't load ${type.name}'s menu. Close and try again.`
              : null
            : byCategory
              ? `${type.name} hides the whole ${categoryName ?? "category"}${categoryName ? " category" : ""}. Change that in Customer Types.`
              : null;
          const id = `product-shown-to-type-${type.id}`;
          return (
            <li key={type.id} data-testid={`row-product-type-${type.id}`}>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={id}
                  checked={detail !== undefined && isShownState(state)}
                  disabled={!detail || byCategory}
                  onCheckedChange={(checked) => onChoice(type.id, checked === true)}
                  data-testid={`checkbox-product-type-${type.id}`}
                />
                <Label htmlFor={id} className={`font-normal ${byCategory ? "text-gray-400" : ""}`}>
                  Shown to {type.name}
                </Label>
              </div>
              {note && (
                <p
                  className={`ml-6 mt-0.5 text-xs ${failed ? "text-red-600" : "text-amber-700"}`}
                  data-testid={`text-product-type-note-${type.id}`}
                >
                  {note}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
